import { fontPixelMapV2, fontPixelMapSmall } from '../constants.js';
import { c, canvas } from '../setup.js';
import { spriteStore } from '../images.js';

export default class PixelText {
    constructor({
        text = '',
        sizeMultiplier = 1,
        color = PixelText.blackTextColor,
        shadowColor = PixelText.whiteShadowColor,
        position = { x: 0, y: 0 },
        cursor = null,
        maxWidth = null,
        lineHeight = 6,
        textAlign = 'left',
        shadow = true,
        shadowOffset = { x: 1, y: 1 },
        rightShadowOffset = { x: 1, y: 0 }, // Right-only shadow
        downShadowOffset = { x: 0, y: 1 }, // Down-only shadow
        fontType = 'small', // 'large' (5x10) or 'small' (4x8)
        includeBounds = false,
        useCursor = false,
        canScroll = false,
        variableWidth = true, // NEW: Enable variable character widths
        minCharSpacing = 1, // NEW: Minimum spacing between characters (in pixels)
        visible = true,
        showContinueArrow = true, // Whether to show continue reading arrow
    }) {
        this.text = text;
        this.sizeMultiplier = sizeMultiplier;
        this.color = color;
        this.position = position;
        this.originalPosition = { ...position }; // Store original position for scrolling
        this.cursor = cursor !== null ? cursor : text.length;
        this.maxWidth = maxWidth;
        this.lineHeight = lineHeight;
        this.textAlign = textAlign;
        this.shadow = shadow;
        this.shadowColor = shadowColor;
        this.shadowOffset = shadowOffset;
        this.rightShadowOffset = rightShadowOffset;
        this.downShadowOffset = downShadowOffset;
        this.fontType = fontType;
        this.includeBounds = includeBounds;
        this.useCursor = useCursor;
        this.canScroll = canScroll;
        this.variableWidth = variableWidth; // NEW
        this.minCharSpacing = minCharSpacing; // NEW
        this.visible = visible;
        this.showContinueArrow = showContinueArrow;

        if (color === 'black') {
            this.color = PixelText.blackTextColor;
            this.shadowColor = PixelText.whiteShadowColor;
        } else if (color === 'white') {
            this.color = PixelText.whiteTextColor;
            this.shadowColor = PixelText.blackShadowColor;
        } else if (color === 'blue') {
            this.color = PixelText.blueTextColor;
            this.shadowColor = PixelText.blueShadowColor;
        } else if (color === 'red') {
            this.color = PixelText.redTextColor;
            this.shadowColor = PixelText.redShadowColor;
        }

        // Font-specific dimensions
        if (this.fontType === 'small') {
            this.charWidth = 4 * this.sizeMultiplier;
            this.charHeight = 8 * this.sizeMultiplier;
            this.charSpacing = 5 * this.sizeMultiplier; // includes 1px gap for small font
            this.fontMap = fontPixelMapSmall;
        } else {
            this.charWidth = 5 * this.sizeMultiplier;
            this.charHeight = 10 * this.sizeMultiplier;
            this.charSpacing = 6 * this.sizeMultiplier; // includes 1px gap for large font
            this.fontMap = fontPixelMapV2;
        }

        // NEW: Calculate variable character widths
        this.characterWidths = this.variableWidth ? this.calculateCharacterWidths() : {};

        // Scrolling properties
        this.scrollingState = {
            isScrolling: false,
            hasStartedThirdLine: false,
            scrollOffset: 0,
            targetScrollOffset: 0,
            scrollSpeed: 1, // pixels per frame
            waitingForUserInput: false, // Indicates if scrolling is paused waiting for user to press A
            pendingScrollOffset: 0, // Stores the target scroll offset when user input is required
            currentVisibleLines: [1, 2], // Tracks which lines are currently visible (1-indexed)
            totalLines: 0, // Total number of lines in the text
        };

        // Bounding box for clipping - shows only 2 lines at a time, with extra space for shadows
        this.boundingBox = {
            x: this.position.x,
            y: this.position.y,
            width: this.maxWidth || Infinity,
            height: 2 * this.charHeight + this.lineHeight + 1, // Height for exactly 2 lines + 1px for shadow
        };

        // Continue reading arrow - only initialize if canScroll is enabled
        if (this.canScroll) {
            this.continueReadingArrow = spriteStore.store.continueReadingArrow;
            // Position at bottom-right corner of screen
            this.continueReadingArrow.position = {
                x: canvas.width - 22,
                y: canvas.height - 21,
            };
            this.continueReadingArrow.visible = false;
        } else {
            this.continueReadingArrow = null;
        }

        // Pre-calculate all pixel positions once
        this.precomputedPixels = this.precomputePixels();
        this.precomputedShadowPixels = this.shadow ? this.precomputeShadowPixels() : [];
        this.precomputedRightShadowPixels = this.shadow ? this.precomputeRightShadowPixels() : [];
        this.precomputedDownShadowPixels = this.shadow ? this.precomputeDownShadowPixels() : [];
    }

    // NEW: Calculate the actual width of each character by detecting empty trailing columns
    calculateCharacterWidths() {
        const widths = {};
        const defaultCharPixelWidth = this.fontType === 'small' ? 4 : 5;

        for (const [char, pixelArray] of Object.entries(this.fontMap)) {
            // Use this character's data width (allows e.g. 5-wide glyphs in small font)
            const maxCols = pixelArray[0]?.length ?? defaultCharPixelWidth;
            let actualWidth = maxCols;

            // Check each column from right to left to find the rightmost non-empty column
            for (let col = maxCols - 1; col >= 0; col--) {
                let hasPixel = false;

                // Check if this column has any pixels in any row
                for (let row = 0; row < pixelArray.length; row++) {
                    if (pixelArray[row][col] === '1') {
                        hasPixel = true;
                        break;
                    }
                }

                if (hasPixel) {
                    actualWidth = col + 1; // +1 because col is 0-indexed
                    break;
                }

                // If we've checked all columns and found no pixels, keep at least 1 pixel width
                if (col === 0 && !hasPixel) {
                    actualWidth = 1;
                }
            }

            // Apply size multiplier and ensure minimum spacing
            widths[char] = Math.max(
                actualWidth * this.sizeMultiplier + this.minCharSpacing * this.sizeMultiplier,
                this.minCharSpacing * this.sizeMultiplier
            );
        }

        return widths;
    }

    // NEW: Get the effective spacing for a character (variable or fixed width)
    getCharacterSpacing(char) {
        if (this.variableWidth && this.characterWidths[char] !== undefined) {
            return this.characterWidths[char];
        }
        return this.charSpacing;
    }

    precomputePixels() {
        const pixels = [];

        if (this.maxWidth) {
            // Pre-calculate ALL line breaks for the full text first
            const allLines = this.calculateLineBreaks(this.text);

            // Check if we should start scrolling (3rd+ line detected)
            this.checkForScrollNeeded(allLines);

            // Then apply cursor logic to the pre-calculated lines
            this.processPreCalculatedLines(allLines, this.cursor, (char, charX, charY) => {
                this.addCharacterPixels(char, charX, charY, pixels);
            });
        } else {
            // Single line logic
            const renderedText = this.text.slice(0, this.cursor);
            this.processSingleLine(renderedText, (char, charX, charY) => {
                this.addCharacterPixels(char, charX, charY, pixels);
            });
        }

        return pixels;
    }

    precomputeShadowPixels() {
        const shadowPixels = [];

        if (this.maxWidth) {
            // Pre-calculate ALL line breaks for the full text first
            const allLines = this.calculateLineBreaks(this.text);

            // Then apply cursor logic to the pre-calculated lines
            this.processPreCalculatedLines(allLines, this.cursor, (char, charX, charY) => {
                const shadowX = charX + this.shadowOffset.x;
                const shadowY = charY + this.shadowOffset.y;
                this.addCharacterPixels(char, shadowX, shadowY, shadowPixels);
            });
        } else {
            // Single line logic for shadow
            const renderedText = this.text.slice(0, this.cursor);
            this.processSingleLine(renderedText, (char, charX, charY) => {
                const shadowX = charX + this.shadowOffset.x;
                const shadowY = charY + this.shadowOffset.y;
                this.addCharacterPixels(char, shadowX, shadowY, shadowPixels);
            });
        }

        return shadowPixels;
    }

    precomputeRightShadowPixels() {
        const rightShadowPixels = [];

        if (this.maxWidth) {
            // Pre-calculate ALL line breaks for the full text first
            const allLines = this.calculateLineBreaks(this.text);

            // Then apply cursor logic to the pre-calculated lines
            this.processPreCalculatedLines(allLines, this.cursor, (char, charX, charY) => {
                const shadowX = charX + this.rightShadowOffset.x;
                const shadowY = charY + this.rightShadowOffset.y;
                this.addCharacterPixels(char, shadowX, shadowY, rightShadowPixels);
            });
        } else {
            // Single line logic for right shadow
            const renderedText = this.text.slice(0, this.cursor);
            this.processSingleLine(renderedText, (char, charX, charY) => {
                const shadowX = charX + this.rightShadowOffset.x;
                const shadowY = charY + this.rightShadowOffset.y;
                this.addCharacterPixels(char, shadowX, shadowY, rightShadowPixels);
            });
        }

        return rightShadowPixels;
    }

    precomputeDownShadowPixels() {
        const downShadowPixels = [];

        if (this.maxWidth) {
            // Pre-calculate ALL line breaks for the full text first
            const allLines = this.calculateLineBreaks(this.text);

            // Then apply cursor logic to the pre-calculated lines
            this.processPreCalculatedLines(allLines, this.cursor, (char, charX, charY) => {
                const shadowX = charX + this.downShadowOffset.x;
                const shadowY = charY + this.downShadowOffset.y;
                this.addCharacterPixels(char, shadowX, shadowY, downShadowPixels);
            });
        } else {
            // Single line logic for down shadow
            const renderedText = this.text.slice(0, this.cursor);
            this.processSingleLine(renderedText, (char, charX, charY) => {
                const shadowX = charX + this.downShadowOffset.x;
                const shadowY = charY + this.downShadowOffset.y;
                this.addCharacterPixels(char, shadowX, shadowY, downShadowPixels);
            });
        }

        return downShadowPixels;
    }

    checkForScrollNeeded(lines) {
        if (!this.maxWidth) {
            this.scrollingState.totalLines = 1;
            this.scrollingState.currentVisibleLines = [1, 1];
            this.scrollingState.waitingForUserInput = false;
            return;
        }

        // Store total lines
        this.scrollingState.totalLines = lines.length;

        // Calculate which lines are currently visible based on scroll offset
        const lineHeight = this.charHeight + this.lineHeight;
        const scrollOffset = this.scrollingState.scrollOffset;
        const firstVisibleLine = Math.max(1, Math.floor(-scrollOffset / lineHeight) + 1);
        const lastVisibleLine = Math.min(this.scrollingState.totalLines, firstVisibleLine + 1);
        this.scrollingState.currentVisibleLines = [firstVisibleLine, lastVisibleLine];

        // If we have 2 or fewer lines, no scrolling needed
        if (lines.length <= 2) {
            this.scrollingState.waitingForUserInput = false;
            return;
        }

        // Only check for scrolling if cursor has advanced (not at start)
        // This prevents blocking cursor advancement when text is first set
        if (this.cursor === 0) {
            this.scrollingState.waitingForUserInput = false;
            return;
        }

        // Check if cursor has reached content beyond the 2 visible lines
        let totalCharIndex = 0;
        let needsScroll = false;

        for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
            const line = lines[lineIndex];
            const lineEndIndex = totalCharIndex + line.length;

            // If cursor is on or past a line that's beyond the second visible line
            // We need to check if cursor has actually reached this line (not just that it exists)
            if (lineIndex >= lastVisibleLine) {
                // Check if cursor has reached characters in this line
                if (totalCharIndex < this.cursor) {
                    needsScroll = true;
                    break;
                }
            }

            totalCharIndex = lineEndIndex + 1; // +1 for space between words
        }

        // If we need to scroll and haven't started waiting for user input yet, and not currently scrolling
        if (needsScroll && !this.scrollingState.waitingForUserInput && !this.scrollingState.isScrolling) {
            // Calculate the scroll offset needed to show the next line
            const scrollAmount = lineHeight;
            this.scrollingState.waitingForUserInput = true;
            this.scrollingState.pendingScrollOffset = this.scrollingState.scrollOffset - scrollAmount;
        } else if (!needsScroll) {
            // If we don't need to scroll anymore, clear the waiting state
            this.scrollingState.waitingForUserInput = false;
        }
    }

    // Legacy method name for compatibility
    checkForThirdLine(lines) {
        this.checkForScrollNeeded(lines);
    }

    processPreCalculatedLines(lines, cursor, charCallback) {
        let currentY = this.position.y;
        let totalCharIndex = 0;

        for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
            const line = lines[lineIndex];

            // Check how many characters from this line should be rendered based on cursor
            const lineStartIndex = totalCharIndex;
            const lineEndIndex = totalCharIndex + line.length;

            if (lineStartIndex < cursor) {
                // Determine how much of this line to render
                const charactersToRender = Math.min(cursor - lineStartIndex, line.length);
                const visiblePart = line.substring(0, charactersToRender);

                if (visiblePart.length > 0) {
                    this.processLineCharacters(visiblePart, this.position.x, currentY, charCallback);
                }
            }

            // Move to next line
            currentY += this.charHeight + this.lineHeight;
            totalCharIndex = lineEndIndex + 1; // +1 for the space between words

            // Stop if we've rendered past the cursor
            if (lineStartIndex >= cursor) break;
        }
    }

    calculateLineBreaks(text) {
        const paragraphs = text.split('\n');
        const lines = [];

        for (const paragraph of paragraphs) {
            if (paragraph.length === 0) {
                lines.push('');
                continue;
            }

            const words = paragraph.split(' ');
            let currentLine = '';

            for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
                const word = words[wordIndex];
                const testLine = currentLine + (currentLine ? ' ' : '') + word;
                const testLineWidth = this.measureTextWidth(testLine);

                if (testLineWidth > this.maxWidth && currentLine.length > 0) {
                    lines.push(currentLine);
                    currentLine = word;
                } else {
                    currentLine = testLine;
                }
            }

            if (currentLine.length > 0) {
                lines.push(currentLine);
            }
        }

        return lines;
    }

    processSingleLine(text, charCallback) {
        const lines = text.split('\n');
        let currentY = this.position.y;
        for (const line of lines) {
            this.processLineCharacters(line, this.position.x, currentY, charCallback);
            currentY += this.charHeight + this.lineHeight;
        }
    }

    // MODIFIED: Updated to use variable character spacing
    processLineCharacters(lineText, startX, y, charCallback) {
        let adjustedX = startX;

        // Handle text alignment
        if (this.textAlign === 'center') {
            const lineWidth = this.measureTextWidth(lineText);
            adjustedX = Math.round(startX - lineWidth / 2);
        } else if (this.textAlign === 'right') {
            const lineWidth = this.measureTextWidth(lineText);
            adjustedX = Math.round(startX - lineWidth);
        }

        // Process each character in the line with variable spacing
        let currentX = adjustedX;
        for (let i = 0; i < lineText.length; i++) {
            const char = lineText[i];
            charCallback(char, currentX, y);

            // Move to next character position using variable or fixed spacing
            currentX += this.getCharacterSpacing(char);
        }
    }

    addCharacterPixels(character, charX, charY, pixelsArray) {
        const pixelArray = this.fontMap[character];
        if (!pixelArray) return;

        const defaultCharPixelWidth = this.fontType === 'small' ? 4 : 5;
        const charCols = pixelArray[0]?.length ?? defaultCharPixelWidth;

        pixelArray.forEach((pixelRowString, rowIndex) => {
            for (let colIndex = 0; colIndex < charCols; colIndex++) {
                if (pixelRowString[colIndex] === '1') {
                    // Apply size multiplier
                    for (let sy = 0; sy < this.sizeMultiplier; sy++) {
                        for (let sx = 0; sx < this.sizeMultiplier; sx++) {
                            const pixelX = charX + colIndex * this.sizeMultiplier + sx;
                            const pixelY = charY + rowIndex * this.sizeMultiplier + sy;

                            // Only add pixel if it's within the bounding box
                            if (this.includeBounds) {
                                if (this.isPixelInBounds(pixelX, pixelY)) {
                                    pixelsArray.push({
                                        x: pixelX,
                                        y: pixelY,
                                    });
                                }
                            } else {
                                pixelsArray.push({
                                    x: pixelX,
                                    y: pixelY,
                                });
                            }
                        }
                    }
                }
            }
        });
    }

    isPixelInBounds(pixelX, pixelY) {
        return (
            pixelX >= this.boundingBox.x &&
            pixelX < this.boundingBox.x + this.boundingBox.width &&
            pixelY >= this.boundingBox.y &&
            pixelY < this.boundingBox.y + this.boundingBox.height
        );
    }

    // MODIFIED: Updated to use variable character spacing
    measureTextWidth(text) {
        if (!this.variableWidth) {
            // Original fixed-width calculation
            return text.length * this.charSpacing;
        }

        // Variable width calculation
        let totalWidth = 0;
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            totalWidth += this.getCharacterSpacing(char);
        }
        return totalWidth;
    }

    getWidth() {
        if (this.maxWidth) {
            // For wrapped text, find the widest line from the complete text layout
            const lines = this.calculateLineBreaks(this.text);
            let maxLineWidth = 0;
            let totalCharIndex = 0;

            for (const line of lines) {
                // Only consider lines that have visible characters (within cursor range)
                if (totalCharIndex < this.cursor) {
                    const charactersToRender = Math.min(this.cursor - totalCharIndex, line.length);
                    if (charactersToRender > 0) {
                        const visiblePart = line.substring(0, charactersToRender);
                        const lineWidth = this.measureTextWidth(visiblePart);
                        maxLineWidth = Math.max(maxLineWidth, lineWidth);
                    }
                }
                totalCharIndex += line.length + 1; // +1 for space

                if (totalCharIndex > this.cursor) break;
            }

            return maxLineWidth;
        } else {
            const renderedText = this.text.slice(0, this.cursor);
            return this.measureTextWidth(renderedText);
        }
    }

    getHeight() {
        if (this.maxWidth) {
            // Calculate number of lines that contain visible characters
            const lines = this.calculateLineBreaks(this.text);
            let visibleLineCount = 0;
            let totalCharIndex = 0;

            for (const line of lines) {
                if (totalCharIndex < this.cursor) {
                    visibleLineCount++;
                }
                totalCharIndex += line.length + 1; // +1 for space

                if (totalCharIndex > this.cursor) break;
            }

            return visibleLineCount * this.charHeight + (visibleLineCount - 1) * this.lineHeight;
        } else {
            return this.charHeight;
        }
    }

    // Main function to check if scrolling is active
    isScrolling() {
        return this.scrollingState.isScrolling;
    }

    // Check if waiting for user input to continue scrolling
    waitingForUserInput() {
        return this.scrollingState.waitingForUserInput;
    }

    // Request scroll continuation (called when user presses A)
    requestScroll() {
        if (this.scrollingState.waitingForUserInput) {
            this.scrollingState.waitingForUserInput = false;
            this.scrollingState.isScrolling = true;
            this.scrollingState.targetScrollOffset = this.scrollingState.pendingScrollOffset;
            return true;
        }
        return false;
    }

    // Check if there are more lines to scroll to
    canScrollMore() {
        if (!this.maxWidth) return false;
        const lines = this.calculateLineBreaks(this.text);
        const [, lastVisible] = this.scrollingState.currentVisibleLines;
        return lastVisible < lines.length;
    }

    // Get current visible line range
    getCurrentVisibleLineRange() {
        return this.scrollingState.currentVisibleLines;
    }

    // Check if continue arrow should be shown
    shouldShowContinueArrow() {
        // Only show arrow if scrolling is enabled and showContinueArrow option is true
        if (!this.canScroll || !this.continueReadingArrow || !this.showContinueArrow) {
            return false;
        }
        // Show arrow if waiting for user input to scroll
        if (this.scrollingState.waitingForUserInput) {
            return true;
        }
        // Show arrow if text is complete and there are more lines to scroll
        if (this.cursor >= this.text.length && this.canScrollMore()) {
            return true;
        }
        // Show arrow if text is complete (for finishing reading)
        if (this.cursor >= this.text.length && !this.scrollingState.isScrolling) {
            return true;
        }
        return false;
    }

    // Update scrolling animation (call this every frame)
    updateScrolling() {
        if (!this.canScroll) {
            return;
        }

        if (this.cursor === 0) {
            this.resetScrolling();
        }

        // Don't animate scrolling if waiting for user input
        if (this.scrollingState.waitingForUserInput) {
            return;
        }

        if (!this.scrollingState.isScrolling) return;

        // Move towards target scroll offset
        if (this.scrollingState.scrollOffset > this.scrollingState.targetScrollOffset) {
            this.scrollingState.scrollOffset -= this.scrollingState.scrollSpeed;

            // Ensure we don't overshoot
            if (this.scrollingState.scrollOffset <= this.scrollingState.targetScrollOffset) {
                this.scrollingState.scrollOffset = this.scrollingState.targetScrollOffset;
                this.scrollingState.isScrolling = false; // Stop scrolling

                // After scrolling completes, check if more scrolling is needed
                if (this.maxWidth) {
                    const lines = this.calculateLineBreaks(this.text);
                    this.checkForScrollNeeded(lines);
                }
            }
        }

        // Update position based on scroll offset
        this.position.y = this.originalPosition.y + this.scrollingState.scrollOffset;
    }

    draw() {
        // Update scrolling animation
        this.updateScrolling();

        if (!this.visible) {
            return;
        }

        if (!this.useCursor) {
            this.cursor = 1000;
        }

        // Only recompute if cursor or text has changed
        // For better performance, you might want to add dirty checking here
        this.precomputedPixels = this.precomputePixels();

        if (this.shadow) {
            this.precomputedShadowPixels = this.precomputeShadowPixels();
            this.precomputedRightShadowPixels = this.precomputeRightShadowPixels();
            this.precomputedDownShadowPixels = this.precomputeDownShadowPixels();
        }

        // Draw shadows first (behind main text)
        if (this.shadow) {
            c.fillStyle = this.shadowColor;

            // Draw diagonal shadow (bottom-right)
            if (this.precomputedShadowPixels.length > 0) {
                c.beginPath();
                this.precomputedShadowPixels.forEach((pixel) => {
                    c.rect(pixel.x, pixel.y, 1, 1);
                });
                c.fill();
            }

            // Draw right-only shadow
            if (this.precomputedRightShadowPixels.length > 0) {
                c.beginPath();
                this.precomputedRightShadowPixels.forEach((pixel) => {
                    c.rect(pixel.x, pixel.y, 1, 1);
                });
                c.fill();
            }

            // Draw down-only shadow
            if (this.precomputedDownShadowPixels.length > 0) {
                c.beginPath();
                this.precomputedDownShadowPixels.forEach((pixel) => {
                    c.rect(pixel.x, pixel.y, 1, 1);
                });
                c.fill();
            }
        }

        // Draw main text
        if (this.precomputedPixels.length === 0) return;

        c.fillStyle = this.color;
        c.beginPath();
        this.precomputedPixels.forEach((pixel) => {
            c.rect(pixel.x, pixel.y, 1, 1);
        });
        c.fill();

        // Update and draw continue reading arrow (only if canScroll is enabled and text exists)
        if (
            this.canScroll &&
            this.continueReadingArrow &&
            this.shouldShowContinueArrow() &&
            this.text.length > 0
        ) {
            // Position arrow at bottom-right corner of screen
            this.continueReadingArrow.position.x = canvas.width - 22;
            this.continueReadingArrow.position.y = canvas.height - 21;
            this.continueReadingArrow.visible = true;
        } else if (this.continueReadingArrow) {
            this.continueReadingArrow.visible = false;
        }

        // Draw continue reading arrow
        if (this.canScroll && this.continueReadingArrow && this.continueReadingArrow.visible) {
            this.continueReadingArrow.draw();
        }
    }

    // Method to update cursor (useful for typewriter effects)
    setCursor(newCursor) {
        if (this.useCursor) {
            this.cursor = Math.max(0, Math.min(newCursor, this.text.length));
        } else {
            this.cursor = 1000;
        }
        // Pixels will be recomputed on next draw()
    }

    // Method to update text
    setText(newText) {
        this.text = newText;
        this.cursor = Math.min(this.cursor, newText.length);

        // Reset scrolling state when text changes
        this.scrollingState = {
            isScrolling: false,
            hasStartedThirdLine: false,
            scrollOffset: 0,
            targetScrollOffset: 0,
            scrollSpeed: 1,
            waitingForUserInput: false,
            pendingScrollOffset: 0,
            currentVisibleLines: [1, 2],
            totalLines: 0,
        };
        this.position.y = this.originalPosition.y;

        // Recalculate character widths if variable width is enabled
        if (this.variableWidth) {
            this.characterWidths = this.calculateCharacterWidths();
        }

        // Pixels will be recomputed on next draw()
    }

    // Method to update color
    setColor(color, shadowColor = null) {
        if (color === 'black') {
            this.color = PixelText.blackTextColor;
            this.shadowColor = PixelText.whiteShadowColor;
        } else if (color === 'white') {
            this.color = PixelText.whiteTextColor;
            this.shadowColor = PixelText.blackShadowColor;
        } else if (color === 'blue') {
            this.color = PixelText.blueTextColor;
            this.shadowColor = PixelText.blueShadowColor;
        } else if (color === 'red') {
            this.color = PixelText.redTextColor;
            this.shadowColor = PixelText.redShadowColor;
        } else {
            // Use provided color directly, and shadowColor if provided
            this.color = color;
            if (shadowColor !== null) {
                this.shadowColor = shadowColor;
            }
        }
        // Pixels will be recomputed on next draw()
    }

    // Method to reset scrolling (useful if you want to manually reset)
    resetScrolling() {
        this.scrollingState = {
            isScrolling: false,
            hasStartedThirdLine: false,
            scrollOffset: 0,
            targetScrollOffset: 0,
            scrollSpeed: 1,
            waitingForUserInput: false,
            pendingScrollOffset: 0,
            currentVisibleLines: [1, 2],
            totalLines: 0,
        };
        this.position.y = this.originalPosition.y;
    }

    // Method to reset text to original position (useful when re-reading text)
    resetToOriginalPosition() {
        this.scrollingState = {
            isScrolling: false,
            hasStartedThirdLine: false,
            scrollOffset: 0,
            targetScrollOffset: 0,
            scrollSpeed: 1,
            waitingForUserInput: false,
            pendingScrollOffset: 0,
            currentVisibleLines: [1, 2],
            totalLines: 0,
        };
        this.position.y = this.originalPosition.y;
        this.cursor = 0; // Reset cursor to beginning

        // Force recomputation of pixels
        this.precomputedPixels = this.precomputePixels();
        if (this.shadow) {
            this.precomputedShadowPixels = this.precomputeShadowPixels();
            this.precomputedRightShadowPixels = this.precomputeRightShadowPixels();
            this.precomputedDownShadowPixels = this.precomputeDownShadowPixels();
        }
    }

    // Method to set scroll speed (pixels per frame)
    setScrollSpeed(speed) {
        this.scrollingState.scrollSpeed = speed;
    }

    // Method to change font type
    setFontType(fontType) {
        this.fontType = fontType;

        // Update font-specific dimensions
        if (this.fontType === 'small') {
            this.charWidth = 4 * this.sizeMultiplier;
            this.charHeight = 8 * this.sizeMultiplier;
            this.charSpacing = 5 * this.sizeMultiplier;
            this.fontMap = fontPixelMapSmall;
        } else {
            this.charWidth = 5 * this.sizeMultiplier;
            this.charHeight = 10 * this.sizeMultiplier;
            this.charSpacing = 6 * this.sizeMultiplier;
            this.fontMap = fontPixelMapV2;
        }

        // Recalculate character widths if variable width is enabled
        if (this.variableWidth) {
            this.characterWidths = this.calculateCharacterWidths();
        }

        // Update bounding box
        this.boundingBox.height = 2 * this.charHeight + this.lineHeight + 1;

        // Force recomputation
        this.precomputedPixels = this.precomputePixels();
        if (this.shadow) {
            this.precomputedShadowPixels = this.precomputeShadowPixels();
            this.precomputedRightShadowPixels = this.precomputeRightShadowPixels();
            this.precomputedDownShadowPixels = this.precomputeDownShadowPixels();
        }
    }

    // NEW: Method to toggle variable width mode
    setVariableWidth(enabled) {
        this.variableWidth = enabled;

        if (enabled) {
            this.characterWidths = this.calculateCharacterWidths();
        } else {
            this.characterWidths = {};
        }

        // Force recomputation
        this.precomputedPixels = this.precomputePixels();
        if (this.shadow) {
            this.precomputedShadowPixels = this.precomputeShadowPixels();
            this.precomputedRightShadowPixels = this.precomputeRightShadowPixels();
            this.precomputedDownShadowPixels = this.precomputeDownShadowPixels();
        }
    }

    // NEW: Method to set minimum character spacing
    setMinCharSpacing(spacing) {
        this.minCharSpacing = spacing;

        if (this.variableWidth) {
            this.characterWidths = this.calculateCharacterWidths();

            // Force recomputation
            this.precomputedPixels = this.precomputePixels();
            if (this.shadow) {
                this.precomputedShadowPixels = this.precomputeShadowPixels();
                this.precomputedRightShadowPixels = this.precomputeRightShadowPixels();
                this.precomputedDownShadowPixels = this.precomputeDownShadowPixels();
            }
        }
    }

    // NEW: Method to get calculated character widths (for debugging)
    getCharacterWidths() {
        return this.characterWidths;
    }

    // Static color constants (similar to your Text class)
    static blackTextColor = 'rgba(60, 60, 60, 1)';
    static whiteTextColor = 'rgba(255, 255, 255, 1)';
    static shadowTextColor = 'rgba(174, 174, 167, 1)';
    static blackShadowColor = 'rgba(17, 35, 46, 1)';
    static whiteShadowColor = 'rgba(174, 174, 167, 1)';
    static redTextColor = '#ffdb73';       // Front color for "red" style
    static redShadowColor = '#e70808';    // Shadow for "red" style
    static blueTextColor = '#6090d0';      // Blue hue
    static blueShadowColor = 'rgba(30, 60, 120, 1)'; // Darker blue shadow
}
